import type { MainToWorkletMessage, WorkletToMainMessage } from "./audio-protocol";
import { AudioVisualBridge } from "./modulation";
import type { EngineModulationRoute, SequencerConfig, SynthParameters } from "./types";

export interface AudioEngineCallbacks {
  onStateChange?: (state: AudioContextState) => void;
  onMessage?: (message: WorkletToMainMessage) => void;
  onError?: (error: Error) => void;
}

/** Owns the gesture-gated Web Audio graph and its single synthesis worklet. */
export class AudioEngine {
  readonly bridge = new AudioVisualBridge();

  #context?: AudioContext;
  #node?: AudioWorkletNode;
  #master?: GainNode;
  #analyser?: AnalyserNode;
  #recordingDestination?: MediaStreamAudioDestinationNode;
  #callbacks: AudioEngineCallbacks;
  #workletUrl: string;
  #seed: number;
  #starting?: Promise<void>;
  #messageSequence = 0;

  constructor(workletUrl = "/crtvar-worklet.js", seed = 0x43525456, callbacks: AudioEngineCallbacks = {}) {
    this.#workletUrl = workletUrl;
    this.#seed = seed >>> 0;
    this.#callbacks = callbacks;
  }

  get context(): AudioContext | undefined {
    return this.#context;
  }

  get analyser(): AnalyserNode | undefined {
    return this.#analyser;
  }

  get sampleRate(): number {
    return this.#context?.sampleRate ?? 0;
  }

  get state(): AudioContextState | "idle" {
    return this.#context?.state ?? "idle";
  }

  get recordingStream(): MediaStream | undefined {
    return this.#recordingDestination?.stream;
  }

  async start(): Promise<void> {
    if (this.#node && this.#context) {
      if (this.#context.state === "suspended") await this.#context.resume();
      return;
    }
    if (this.#starting) return this.#starting;
    this.#starting = this.#initialize();
    try {
      await this.#starting;
    } finally {
      this.#starting = undefined;
    }
  }

  async #initialize(): Promise<void> {
    if (typeof AudioContext === "undefined") throw new Error("Web Audio is not supported by this browser");
    const context = new AudioContext({ latencyHint: "interactive" });
    this.#context = context;
    context.addEventListener("statechange", () => this.#callbacks.onStateChange?.(context.state));
    try {
      await context.audioWorklet.addModule(this.#workletUrl);
      const node = new AudioWorkletNode(context, "crtvar-engine", {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
        processorOptions: { maxVoices: 4 },
      });
      const master = context.createGain();
      const analyser = context.createAnalyser();
      const recordingDestination = context.createMediaStreamDestination();
      master.gain.value = 1;
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.72;
      node.connect(master);
      master.connect(analyser);
      analyser.connect(context.destination);
      analyser.connect(recordingDestination);

      node.port.onmessage = (event: MessageEvent<WorkletToMainMessage>) => {
        const message = event.data;
        this.bridge.handleMessage(message);
        if (message.type === "error") this.#callbacks.onError?.(new Error(message.message));
        this.#callbacks.onMessage?.(message);
      };
      node.addEventListener("processorerror", () => {
        this.#callbacks.onError?.(new Error("The CRTVAR audio processor stopped unexpectedly"));
      });

      this.#node = node;
      this.#master = master;
      this.#analyser = analyser;
      this.#recordingDestination = recordingDestination;
      this.post({ type: "init", seed: this.#seed, sharedBuffer: this.bridge.sharedBuffer });
      await context.resume();
    } catch (error) {
      await context.close().catch(() => undefined);
      this.#context = undefined;
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  post(message: MainToWorkletMessage): void {
    if (!this.#node) return;
    this.#messageSequence += 1;
    this.#node.port.postMessage(message);
  }

  setParameters(parameters: Partial<SynthParameters>): void {
    this.post({ type: "parameters", parameters });
  }

  setRoutes(routes: readonly EngineModulationRoute[]): void {
    this.post({ type: "routes", routes });
  }

  setSequencer(config: SequencerConfig): void {
    this.post({ type: "sequencer", config });
  }

  setTransport(running: boolean, reset = false): void {
    this.post({ type: "transport", running, reset });
  }

  noteOn(note: number, velocity: number, owner: string, order: number): void {
    this.post({
      type: "note-on",
      note: Math.max(0, Math.min(127, Math.round(note))),
      velocity: Math.max(0, Math.min(1, velocity)),
      owner,
      order,
    });
  }

  noteOff(note: number, owner: string): void {
    this.post({ type: "note-off", note: Math.round(note), owner });
  }

  allNotesOff(immediate = false): void {
    this.post({ type: "all-notes-off", immediate });
  }

  panic(): void {
    this.post({ type: "panic" });
    const context = this.#context;
    const master = this.#master;
    if (context && master) {
      master.gain.cancelScheduledValues(context.currentTime);
      master.gain.setValueAtTime(master.gain.value, context.currentTime);
      master.gain.linearRampToValueAtTime(0, context.currentTime + 0.025);
    }
  }

  async resumeFromPanic(): Promise<void> {
    const context = this.#context;
    const master = this.#master;
    if (!context || !master) return;
    if (context.state === "suspended") await context.resume();
    this.post({ type: "resume" });
    master.gain.cancelScheduledValues(context.currentTime);
    master.gain.setValueAtTime(master.gain.value, context.currentTime);
    master.gain.linearRampToValueAtTime(1, context.currentTime + 0.06);
  }

  readTimeDomain(target: Float32Array<ArrayBuffer>): void {
    this.#analyser?.getFloatTimeDomainData(target);
  }

  readFrequency(target: Float32Array<ArrayBuffer>): void {
    this.#analyser?.getFloatFrequencyData(target);
  }

  async close(): Promise<void> {
    this.#node?.disconnect();
    this.#master?.disconnect();
    this.#analyser?.disconnect();
    this.#recordingDestination?.disconnect();
    this.#node = undefined;
    this.#master = undefined;
    this.#analyser = undefined;
    this.#recordingDestination = undefined;
    const context = this.#context;
    this.#context = undefined;
    if (context && context.state !== "closed") await context.close();
  }
}
